import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBug, faCheckCircle, faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Api } from '../api';
import './css/ReportIssueModal.css';

const ReportIssueModal = ({ onClose, defaultEmail = '' }) => {
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState(defaultEmail || '');
  const [status, setStatus] = useState('form'); // form | submitting | success
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setError(null);

    const trimmed = description.trim();
    if (trimmed.length < 10) {
      setError('Please describe the issue in a bit more detail (at least a sentence).');
      return;
    }

    setStatus('submitting');
    try {
      await Api.post('report-issue', {
        description: trimmed,
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
        email: email.trim() || undefined,
      });
      if (isMountedRef.current) {
        setStatus('success');
      }
    } catch (err) {
      if (!isMountedRef.current) return;
      const statusCode = err.response?.status;
      let message =
        err.response?.data?.message ||
        err.message ||
        'Could not submit the report. Please try again.';
      if (statusCode === 401) {
        message = 'Please log in again to report an issue.';
      } else if (statusCode === 403) {
        message = 'Only admins can report issues.';
      } else if (statusCode === 503) {
        message = 'Issue reporting is not configured on this environment yet.';
      }
      setError(message);
      setStatus('form');
    }
  };

  const modalContent = (
    <div
      className="report-issue-overlay"
      onClick={(e) => {
        e.stopPropagation();
        if (status !== 'submitting' && onClose) onClose();
      }}
      role="presentation"
    >
      <div
        className="report-issue-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-issue-title"
      >
        {status !== 'submitting' && onClose && (
          <button
            type="button"
            className="report-issue-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}

        {status === 'success' ? (
          <>
            <div className="report-issue-header">
              <FontAwesomeIcon icon={faCheckCircle} className="report-issue-icon success" />
              <h2 id="report-issue-title">Thanks — we got it</h2>
              <p>A fix agent will look at this on staging. Production stays manual.</p>
            </div>
            <button type="button" className="report-issue-button primary" onClick={onClose}>
              Done
            </button>
          </>
        ) : (
          <>
            <div className="report-issue-header">
              <FontAwesomeIcon icon={faBug} className="report-issue-icon" />
              <h2 id="report-issue-title">Report an issue</h2>
              <p>Describe what went wrong — same as chatting with Cursor. We will investigate on staging.</p>
            </div>

            <form className="report-issue-form" onSubmit={handleSubmit}>
              <label className="report-issue-label" htmlFor="report-issue-description">
                What happened?
              </label>
              <textarea
                id="report-issue-description"
                className="report-issue-textarea"
                rows={6}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g. On Preview, tapping play does nothing after I upload audio…"
                disabled={status === 'submitting'}
                autoFocus
              />

              <label className="report-issue-label" htmlFor="report-issue-email">
                Email (optional)
              </label>
              <input
                id="report-issue-email"
                type="email"
                className="report-issue-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="so we can follow up"
                autoComplete="email"
                disabled={status === 'submitting'}
              />

              {error && <div className="report-issue-error">{error}</div>}

              <button
                type="submit"
                className="report-issue-button primary"
                disabled={status === 'submitting'}
              >
                {status === 'submitting' ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Sending…
                  </>
                ) : (
                  'Send report'
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ReportIssueModal;
